import {
	ENDPOINT_URL,
	GET_AP_LIST,
	GET_CLIENT_LIST,
	HOME_DASHBOARD_STATS_API,
} from '../../constants/commonConstants';

/**
 * The reads the agent is allowed to make from this browser.
 *
 * Mirrors the backend's declared sources deliberately. The backend names a
 * source and parameters and never a URL - that is what lets the same query
 * later be run from the backend with a forwarded token instead of here -
 * so the URL has to be written down on this side, and this is where.
 *
 * Two rules hold for every source and are the reason this file exists
 * rather than each handler calling axios directly:
 *
 *   A source lists the fields it will hand back. Everything else is
 *   dropped before the payload leaves the browser. An Access Point row
 *   carries a MAC address, an IP, a serial and a model; a question about
 *   how many are offline needs one of those, and the agent's traces record
 *   what it is given.
 *
 *   A source caps how many rows may travel. The estate runs to five
 *   figures and the answer to "how many" is a number, not a table.
 */

const MAX_ROWS = 100;

export const DATASET_SOURCES = {
	'ap.inventory': {
		url: `${ENDPOINT_URL}${GET_AP_LIST}`,
		rowsField: 'results',
		totalField: 'count',
		// Canvas's own query names, keyed by what the agent calls them.
		filters: {
			group: 'group',
			status: 'status',
			model: 'model',
			firmware: 'firmware',
			search: 'search',
		},
		// What a row may contribute to a count, a grouping, or being opened
		// afterwards. The last of those is why the serial and the name are
		// here: "open the busiest one" needs a record to open, and stripping
		// both meant the backend's exporter produced no references at all
		// from a page that was full of them.
		//
		// Still no MAC and no IP. Neither answers a question about how many
		// and neither is needed to open anything, so neither travels.
		fields: [
			'serial_no', 'ap_name',
			'is_connected', 'ap_model', 'firmware_version', 'group_name',
		],
		maxRows: MAX_ROWS,
	},
	'client.inventory': {
		url: `${ENDPOINT_URL}${GET_CLIENT_LIST}`,
		rowsField: 'results',
		totalField: 'count',
		// Canvas names the client search parameter differently from the
		// Access Point one. That difference belongs here, beside the URL,
		// rather than in anything the agent says.
		filters: {group: 'group', searchfilter: 'searchfilter'},
		// A client is identified by its MAC - there is nothing else stable
		// - so that one does travel, with the hostname to show beside it.
		// The IP does not: it answers nothing here and changes on its own.
		// is_connected is deliberately absent: this list is the connected
		// clients, so the column answers nothing here, and nothing declares
		// a use for it. A field that travels for no declared reason is data
		// reaching the agent's traces for no reason.
		//
		// rssi is here for the opposite reason. "Weak signal" is a declared
		// term - weaker than -70 dBm - and it is declared about clients,
		// which are the only things that have a signal strength. Without
		// the column the one source the term was written for could not be
		// asked the question, so the term was declared and unusable.
		fields: [
			'mac_address', 'hostname',
			'client_band', 'ap_name', 'ssid_name', 'rssi',
		],
		maxRows: MAX_ROWS,
	},
	'dashboard.counts': {
		url: `${ENDPOINT_URL}${HOME_DASHBOARD_STATS_API}`,
		// Already the answer rather than a sample of one, so there is
		// nothing to page and nothing to trim.
		rowsField: null,
		totalField: null,
		filters: {},
		fields: null,
		maxRows: 0,
	},
};

/**
 * Look up a declared source.
 * @param {string} sourceId source the agent named
 * @return {object|null} the source, or null when it is not one we serve
 */
export function datasetSource(sourceId) {
	return Object.prototype.hasOwnProperty.call(DATASET_SOURCES, sourceId)
		? DATASET_SOURCES[sourceId]
		: null;
}
